import React, { useState, useRef, useMemo, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { AtSign, User } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useNostrQuery } from '../hooks/useNostrQuery';
import { useFollows } from '../hooks/useFollows';
import { useAppContext } from '../hooks/useAppContext';

// ── MentionTextarea ─────────────────────────────────────────────
// A textarea with @mention autocomplete that can be used anywhere.
// Fetches community profiles once and suggests them when the user
// types @. Works in reply composers, comment sections, etc.

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Called with pubkeys that were mentioned (for p-tags) */
  onMention?: (pubkey: string, name: string) => void;
  autoFocus?: boolean;
}

export interface MentionTextareaRef {
  focus: () => void;
  textarea: HTMLTextAreaElement | null;
}

const MentionTextarea = forwardRef<MentionTextareaRef, MentionTextareaProps>(({
  value,
  onChange,
  placeholder,
  rows = 2,
  className = '',
  onKeyDown,
  onMention,
  autoFocus,
}, ref) => {
  const { user } = useAppContext();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionDropdown, setMentionDropdown] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [cursorPos, setCursorPos] = useState(0);

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    textarea: textareaRef.current,
  }));

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  // ── Fetch profiles for mention suggestions ──
  const { follows } = useFollows(user?.pubkey);
  const [mentionSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 14);
  const { events: communityPosts } = useNostrQuery({
    filters: [
      // Active BCH Nostr members — #bchnostr is a relay-indexable 't' tag that
      // every in-app post now carries (the 'client' tag isn't filterable on
      // most relays).
      { kinds: [1], '#t': ['bchnostr'], limit: 100, since: mentionSince },
      { kinds: [1], '#t': ['bch'], limit: 100, since: mentionSince },
      { kinds: [1], '#t': ['bitcoincash'], limit: 100, since: mentionSince },
    ],
  });

  const allMentionPubkeys = useMemo(() => {
    const pks = new Set<string>(follows);
    communityPosts.forEach(e => pks.add(e.pubkey));
    return [...pks].slice(0, 80);
  }, [follows.length, communityPosts.length]);

  const [stablePubkeys, setStablePubkeys] = useState<string[]>([]);
  useEffect(() => {
    if (allMentionPubkeys.length > stablePubkeys.length) {
      setStablePubkeys(allMentionPubkeys);
    }
  }, [allMentionPubkeys.length]);

  const { events: mentionProfiles } = useNostrQuery({
    filters: stablePubkeys.length > 0 ? [{ kinds: [0], authors: stablePubkeys, limit: 80 }] : [],
    enabled: stablePubkeys.length > 0,
  });

  // ── Build mention suggestions ──
  const mentionSuggestions = useMemo(() => {
    if (!mentionDropdown) return [];
    const query = mentionQuery.toLowerCase();
    const seen = new Set<string>();
    const results: { pubkey: string; name: string; picture?: string; nip05?: string; isFollow: boolean }[] = [];

    for (const ev of mentionProfiles) {
      if (seen.has(ev.pubkey)) continue;
      seen.add(ev.pubkey);
      try {
        const p = JSON.parse(ev.content);
        const name = p.display_name || p.name || '';
        const nip05 = p.nip05 || '';
        if (query.length === 0 || name.toLowerCase().includes(query) || nip05.toLowerCase().includes(query) || ev.pubkey.startsWith(query)) {
          results.push({
            pubkey: ev.pubkey,
            name: name || ev.pubkey.slice(0, 8),
            picture: p.picture,
            nip05,
            isFollow: follows.includes(ev.pubkey),
          });
        }
      } catch {}
    }

    // Follows first, then alphabetical
    results.sort((a, b) => {
      if (a.isFollow && !b.isFollow) return -1;
      if (!a.isFollow && b.isFollow) return 1;
      return a.name.localeCompare(b.name);
    });

    return results.slice(0, 10);
  }, [mentionDropdown, mentionQuery, mentionProfiles, follows]);

  // ── Handle input with @ detection ──
  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    onChange(val);
    const pos = e.target.selectionStart || 0;
    setCursorPos(pos);

    // Check for @mention trigger
    const before = val.slice(0, pos);
    const atMatch = before.match(/(?:^|[\s(])@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionDropdown(true);
      setMentionIndex(0);
    } else {
      setMentionDropdown(false);
      setMentionQuery('');
    }
  }, [onChange]);

  // ── Insert mention ──
  const insertMention = useCallback((pubkey: string, name: string) => {
    const before = value.slice(0, cursorPos);
    const after = value.slice(cursorPos);
    const atIndex = before.lastIndexOf('@');

    const npub = nip19.npubEncode(pubkey);
    const newContent = before.slice(0, atIndex) + `nostr:${npub} ` + after;
    onChange(newContent);
    setMentionDropdown(false);
    setMentionQuery('');

    // Notify parent for p-tags
    onMention?.(pubkey, name);

    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus();
      const newPos = atIndex + `nostr:${npub} `.length;
      textareaRef.current?.setSelectionRange(newPos, newPos);
    }, 10);
  }, [value, cursorPos, onChange, onMention]);

  // ── Handle keyboard in dropdown ──
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionDropdown && mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(i => Math.min(i + 1, mentionSuggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const s = mentionSuggestions[mentionIndex];
        if (s) insertMention(s.pubkey, s.name);
        return;
      }
    }
    if (e.key === 'Escape' && mentionDropdown) {
      e.preventDefault();
      setMentionDropdown(false);
      setMentionQuery('');
      return;
    }
    onKeyDown?.(e);
  }, [mentionDropdown, mentionSuggestions, mentionIndex, insertMention, onKeyDown]);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        placeholder={placeholder}
        rows={rows}
        className={className}
        onKeyDown={handleKeyDown}
      />

      {/* Mention dropdown */}
      {mentionDropdown && mentionSuggestions.length > 0 && (
        <div className="absolute left-0 bottom-full mb-1 w-72 bg-popover border border-border rounded-lg shadow-xl z-30 max-h-56 overflow-y-auto">
          <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-b border-border flex items-center gap-1">
            <AtSign className="w-3 h-3" /> ↑↓ navigate, Enter to select
          </div>
          {mentionSuggestions.map((s, i) => (
            <button
              key={s.pubkey}
              onClick={() => insertMention(s.pubkey, s.name)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                i === mentionIndex ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
              }`}
            >
              {s.picture ? (
                <img src={s.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <User className="w-3.5 h-3.5 text-primary/60" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate">{s.name}</p>
                {s.nip05 && <p className="text-[10px] text-muted-foreground truncate">{s.nip05}</p>}
              </div>
              {s.isFollow && <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full shrink-0">following</span>}
            </button>
          ))}
        </div>
      )}
      {mentionDropdown && mentionSuggestions.length === 0 && (
        <div className="absolute left-0 bottom-full mb-1 w-64 bg-popover border border-border rounded-lg shadow-xl z-30 p-3">
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <AtSign className="w-3 h-3" />
            {mentionProfiles.length === 0 ? 'Loading contacts...' : mentionQuery.length < 2 ? 'Keep typing to search...' : 'No users found. Try a different name.'}
          </p>
        </div>
      )}
    </div>
  );
});

MentionTextarea.displayName = 'MentionTextarea';
export default MentionTextarea;

